import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { apiFetch } from "@/lib/api/client";
import { memberDiscountPercent } from "@/lib/membership";
import type { BootstrapResponse } from "@/types/bootstrap";
import { useAuth } from "@/lib/auth/AuthProvider";
import { track } from "@/lib/analytics";

export const LABS_EXPERIMENT_KEY =
  import.meta.env.VITE_APP_ENV === "staging"
    ? "portal-blood-testing-staging-v1"
    : import.meta.env.VITE_APP_ENV === "production"
      ? "portal-blood-testing-v1"
      : null;

export type LabEnrollment = {
  experiment_key: string | null;
  variant: string | null;
  enabled: boolean;
  environment: string;
};

export function useLabsExperiment() {
  const { session } = useAuth();
  const query = useQuery({
    // Dev-panel overrides reload the portal. Never seed this
    // query from the shared marketing cookie or a previous signed-in account.
    queryKey: ["experiments", "labs", LABS_EXPERIMENT_KEY, session?.user.id],
    queryFn: () => apiFetch<LabEnrollment>("/me/labs/availability"),
    enabled: Boolean(session?.user.id && LABS_EXPERIMENT_KEY),
    staleTime: 30_000,
    refetchInterval: 30_000,
    retry: false,
  });
  const valid = Boolean(
    session?.user.id &&
    LABS_EXPERIMENT_KEY &&
    !query.isError &&
    query.data?.experiment_key === LABS_EXPERIMENT_KEY &&
    query.data?.environment === import.meta.env.VITE_APP_ENV,
  );
  const variant = valid ? (query.data?.variant ?? null) : null;
  return {
    enabled: valid && query.data?.enabled === true && variant === "variant-a",
    loading: Boolean(
      session?.user.id && LABS_EXPERIMENT_KEY && query.isPending,
    ),
    variant,
  };
}

/** Discovery is gated. Owned records and capability-based paid recovery
 * remain available, and System Members (a blood test every 3 months) always
 * reach their Labs list. */
export function LabsGate({ children }: { children: ReactNode }) {
  const labs = useLabsExperiment();
  // Same query (key, fetch, freshness) as useBootstrap, which the app shell
  // already loads; not imported so this gate stays free of the auth client.
  const bootstrap = useQuery({
    queryKey: ["bootstrap"],
    queryFn: () => apiFetch<BootstrapResponse>("/me/bootstrap"),
    staleTime: 5 * 60_000,
  });
  const member = memberDiscountPercent(bootstrap.data) > 0;
  if (labs.loading || (!labs.enabled && bootstrap.isPending))
    return (
      <div role="status" className="p-20">
        Loading…
      </div>
    );
  return labs.enabled || member ? children : <Navigate to="/app" replace />;
}

type LabEventProperties = {
  source: string;
  product_id?: string;
  purchase_type?: "one_time" | "subscription";
  interval_months?: number;
  step?: number;
  reason?: string;
};

export function useLabEvent() {
  const { variant } = useLabsExperiment();
  return useCallback(
    (event: string, properties: LabEventProperties) => {
      track(event, {
        ...properties,
        environment: import.meta.env.VITE_APP_ENV,
        experiment_key: LABS_EXPERIMENT_KEY,
        variant_key: variant,
        ...(LABS_EXPERIMENT_KEY
          ? { [`$experiment_${LABS_EXPERIMENT_KEY}`]: variant }
          : {}),
      });
    },
    [variant],
  );
}

export function useLabView(event: string, source: string, ready = true) {
  const emit = useLabEvent();
  const sent = useRef(false);
  useEffect(() => {
    if (!ready || sent.current) return;
    sent.current = true;
    emit(event, { source });
  }, [emit, event, source, ready]);
}

export function LabsExperimentExposure() {
  const { variant } = useLabsExperiment();
  const { session } = useAuth();
  const emit = useLabEvent();
  const last = useRef<string | null>(null);
  useEffect(() => {
    if (!variant || !session?.user.id) return;
    const key = `${session.user.id}:${variant}`;
    if (last.current === key) return;
    last.current = key;
    emit("$experiment_viewed", { source: "portal_labs" });
  }, [variant, session?.user.id, emit]);
  return null;
}

export function useLabCardImpression() {
  const ref = useRef<HTMLDivElement>(null);
  const sent = useRef(false);
  const emit = useLabEvent();
  useEffect(() => {
    if (!ref.current || sent.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || sent.current) return;
        sent.current = true;
        emit("blood_test_callout_viewed", { source: "home_card" });
        observer.disconnect();
      },
      { threshold: 0.5 },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [emit]);
  return ref;
}
